// ---------------------------------------------------------------------------
// Domain types
//
// Shared planogram types used by the domain, hooks, visual view and tabular
// view.
// ---------------------------------------------------------------------------

import type {
    WeightOrVolume,
} from '../../model/PlanogramProductModel';

export interface MergeData {
    colspan?: number;
    isMerged?: boolean;
    mergedCells?: string[];
    sourceCell?: CellCoordinates;
    targetCells?: CellCoordinates[];

    mergedMdbIds?: Array<
        string | number | null
    > | null;

    mergedEvaDtsIds?: Array<
        string | number | null
    > | null;

    [key: string]: unknown;
}

export interface CellCoordinates {
    rowIndex: number;
    colIndex: number;
}

export interface ProductOption {
    id?: string | number;
    value?: string | number;
    externalId?: string | number;
    systemId?: string | number;
    externalSystemId?: string | number;

    label?: string;
    name?: string;
    productName?: string;

    ean?: string | number;
    price?: unknown;
    minQuantity?: unknown;
    maxQuantity?: unknown;
    currency?: unknown;

    categoryName?: string | null;
    categoryColor?: string | null;

    hasPhoto?: boolean;
    photoUrl?: string | null;

    /*
     * New structured planogram API representation.
     */
    weightOrVolume?: WeightOrVolume | null;

    /*
     * Legacy camel-case representation used by existing UI components.
     */
    weightVolume?: unknown;
    unitOfMeasurement?: string | null;

    /*
     * Snake-case fallbacks supported by some product responses.
     */
    weight_or_volume?: WeightOrVolume | null;
    weight_volume?: unknown;
    unit_of_measurement?: string | null;

    raw?: unknown;

    [key: string]: unknown;
}

export interface PlanogramProductData {
    id?: string | number | null;
    value?: string | number | null;
    externalId?: string | number | null;
    systemId?: string | number | null;
    externalSystemId?: string | number | null;

    name?: string;
    label?: string;
    productName?: string;

    ean?: string | number | null;
    price?: unknown;
    minQuantity?: unknown;
    maxQuantity?: unknown;
    currency?: unknown;

    categoryName?: string | null;
    categoryColor?: string | null;

    hasPhoto?: boolean;
    photoUrl?: string | null;

    weightOrVolume?: WeightOrVolume | null;
    weightVolume?: unknown;
    unitOfMeasurement?: string | null;

    weight_or_volume?: WeightOrVolume | null;
    weight_volume?: unknown;
    unit_of_measurement?: string | null;

    effectiveDate?: Date | string | null;

    [key: string]: unknown;
}

export interface PlanogramCellData {
    id?: string;
    type?: string;

    product?: PlanogramProductData | null;

    productExternalId?:
        | string
        | number
        | null;

    productName?: string;

    price?: unknown;
    minQuantity?: unknown;
    maxQuantity?: unknown;
    currency?: unknown;

    categoryColor?: string | null;
    categoryName?: string | null;

    ean?: string | number | null;

    hasPhoto?: boolean;
    productPhotoUrl?: string | null;

    /*
     * New structured API representation.
     */
    weightOrVolume?: WeightOrVolume | null;

    /*
     * Legacy properties used by the current UI.
     */
    weightVolume?: unknown;
    unitOfMeasurement?: string | null;

    /*
     * Optional API fallback properties.
     */
    weight_or_volume?: WeightOrVolume | null;
    weight_volume?: unknown;
    unit_of_measurement?: string | null;

    mdbId?: string | number | null;
    evaDtsId?: string | number | null;
    externalId?: string | number | null;

    merged?: boolean;
    mergeCells?: boolean;
    mergeData?: MergeData | null;

    mergedMdbIds?: Array<
        string | number | null
    > | null;

    mergedEvaDtsIds?: Array<
        string | number | null
    > | null;

    effectiveDate?:
        | Date
        | string
        | null;

    [key: string]: unknown;
}

export type PlanogramGrid =
    PlanogramCellData[][];

export interface PlanogramData {
    id?: string;
    externalId?: string;
    uuid?: string;
    name?: string;

    rows: number;
    columns: number;
    cells: PlanogramGrid;

    status?: string;
    createdAt?: string;
    updatedAt?: string;

    systemId?: string | number;
    externalSystemId?: string | number;
    deviceExternalId?: string | number;

    tiedDevicesCount?: number;

    lockResult?: {
        blockedByAnotherUser?: boolean;
        lockedByUser?: {
            userName?: string;
        };
    };

    [key: string]: unknown;
}

export interface LockInfo {
    lockedBy: string;
}

export interface ProductSearchEvent {
    query: string;
}

export interface AssignIdForm {
    rowIndex?: number;
    colIndex?: number;
    mdbId?: string;
    evaDtsId?: string;

    [key: string]: unknown;
}

export const asCellData = (
    value: unknown,
): PlanogramCellData =>
    value as PlanogramCellData;

export type CellKey = string;

export const cellKey = (
    rowIndex: number,
    colIndex: number,
): CellKey =>
    `${rowIndex}-${colIndex}`;

export const parseCellKey = (
    key: CellKey,
): CellCoordinates => {
    const [
        rowString,
        columnString,
    ] = key.split('-');

    return {
        rowIndex:
            Number(rowString),
        colIndex:
            Number(columnString),
    };
};